// client/src/App.js
// client/src/App.js
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
} from "react-router-dom";
import "./App.css";
import { useAuth0 } from "@auth0/auth0-react";
import Tooltip from "./components/Tooltip";

// Import components
import Staff from "./components/Staff";
import CallLists from "./components/CallLists";
import DoctorEligibility from "./components/DoctorEligibility";
import Rules from "./components/Rules";
import Leave from "./components/Leave";
import CallCalendar from "./components/Calendar";
import Metrics from "./components/Metrics";
import NoCallEntries from "./components/NoCallEntries";

function App() {
  const auth0Context = useAuth0();
  const { isLoading, error, user, isAuthenticated, loginWithRedirect, logout } =
    auth0Context;

  // Debug logs to check user and roles
  if (user) {
    // console.log(user);
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-box">
          <h1 className="error-title">Oops! Something went wrong</h1>
          <p className="error-message">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="App">
        <div className="landing-page">
          <div className="landing-content">
            <div className="landing-left">
              <img
                src="/assets/RapportLogo.png"
                alt="Rapport AI Medical Logo"
                className="company-logo"
                style={{ maxWidth: "250px", marginBottom: "2rem" }}
              />
              <h1 className="landing-title">ON-CALL SCHEDULER</h1>
              <p className="landing-description">
                Ready to make on-call scheduling a breeze? Join us and
                experience a smarter, <strong>fairer</strong> way to manage your
                medical team's schedules!
              </p>
              <div className="landing-features">
                <div className="feature">
                  <span className="feature-icon">🤖</span>
                  <span>Smart Schedule Generation</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">⚖️</span>
                  <span>Fair Rotation System</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">📊</span>
                  <span>Insightful Analytics</span>
                </div>
                <div className="feature">
                  <span className="feature-icon">⚙️</span>
                  <span>Customisable Rules & Features</span>
                </div>
              </div>
              <button
                onClick={() => loginWithRedirect()}
                className="landing-login-btn"
              >
                Enter Platform
              </button>
            </div>
            <div className="landing-right">
              <div className="floating-cards">
                <div className="card card-1">
                  <img
                    src="/screenshots/calendar-view.png"
                    alt="Schedule Calendar Interface"
                  />
                </div>
                <div className="card card-2">
                  <img
                    src="/screenshots/analytics-view.png"
                    alt="Analytics Dashboard"
                  />
                </div>
                <div className="card card-3">
                  <img
                    src="/screenshots/staff-view.png"
                    alt="Staff Management"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <div className="app">
          <div className="header-container">
            <div className="header-top">
              <div style={{ display: "flex", alignItems: "center" }}>
                <img
                  src="/assets/RapportLogo.png"
                  alt="Rapport AI Medical Logo"
                  style={{ height: "25px", marginRight: "15px" }}
                />
                <h1 className="app-title">
                  <strong>ON-CALL SCHEDULER</strong>
                </h1>
              </div>
              <div className="user-section">
                {user && (
                  <div className="user-info">
                    <img
                      src={user.picture}
                      alt="Profile"
                      className="user-avatar"
                    />
                    <div className="user-details">
                      <span className="user-email">
                        {user.nickname ? (
                          <>
                            <strong>Username:</strong> {user.nickname}
                          </>
                        ) : (
                          <>
                            <strong>Email:</strong> {user.email}
                          </>
                        )}
                      </span>{" "}
                      <div className="subscription-pill-container">
                        {user[
                          `https://calllistscheduler.netlify.app/roles`
                        ]?.includes("premium") ? (
                          <span className="subscription-pill premium">
                            Premium
                          </span>
                        ) : (
                          <Tooltip>
                            <span className="subscription-pill lite">Lite</span>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <button onClick={() => logout()} className="logout-button">
                  Log Out
                </button>
              </div>
            </div>
            <nav className="nav-container">
              <NavLink
                to="/staff"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Staff
              </NavLink>
              <NavLink
                to="/call-lists"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Call Lists
              </NavLink>
              <NavLink
                to="/doctor-eligibility"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Eligibility
              </NavLink>
              <NavLink
                to="/rules"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Rules
              </NavLink>
              <NavLink
                to="/leave"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Leaves
              </NavLink>
              <NavLink
                to="/no-call-entries"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                No-Calls
              </NavLink>
              <NavLink
                to="/calendar"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Calendar
              </NavLink>
              <NavLink
                to="/metrics"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Metrics
              </NavLink>
            </nav>
          </div>

          <main className="main-content">
            <Routes>
              <Route path="/" element={<Staff />} />
              <Route path="/staff" element={<Staff />} />
              <Route path="/call-lists" element={<CallLists />} />
              <Route
                path="/doctor-eligibility"
                element={<DoctorEligibility />}
              />
              <Route path="/rules" element={<Rules />} />
              <Route path="/leave" element={<Leave />} />
              <Route path="/no-call-entries" element={<NoCallEntries />} />
              <Route path="/calendar" element={<CallCalendar />} />
              <Route path="/metrics" element={<Metrics />} />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;
